// Type-only: this file must stay loadable without pulling in the Angular
// injectable graph, so its unit test can construct it directly.
import type { VirtualKeyboardService } from 'services/virtual-keyboard.service';

// Walk up from an element until we find the thing that scrolls it.
// Used both by the scroll synchroniser below and by the keyboard component's
// own input-focus scroll path, which is why this is a free function rather
// than a method on either of them.
export function findScrollContainer(element: HTMLElement): HTMLElement | null {
  let parent = element.parentElement;
  while (parent) {
    // check if this element is scrollable
    const style = window.getComputedStyle(parent);
    if (parent.classList.contains('scroll-content') ||
        parent.classList.contains('scroll-area') ||
        (style.overflowY === 'scroll' || style.overflowY === 'auto')) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

// How much of the keyboard is visible right now, in pixels.
//
// While the bottom sheet slides open, slides shut, or is dragged by the user,
// it is pushed down the screen with a CSS transform. The element keeps its full
// height throughout, so the height alone says nothing about how much of it the
// user can actually see. Subtracting the transform's vertical offset from the
// height gives the visible part.
//
// Exported for the unit test; nothing outside this file needs it.
export function visibleHeightOf(wrapper: HTMLElement): number {
  const wrapperHeight = wrapper.offsetHeight;
  const transform = window.getComputedStyle(wrapper).transform;

  // if there is no transform, the sheet sits at its natural position and is fully visible
  if (!transform || transform === 'none') {
    return Math.max(0, wrapperHeight);
  }

  // the sheet is animating or being dragged - read the vertical offset out of the transform matrix
  const match = transform.match(/matrix\(([^)]+)\)/);
  if (!match) {
    // couldn't parse the transform, assume fully visible
    return Math.max(0, wrapperHeight);
  }

  const values = match[1].split(',').map(v => parseFloat(v.trim()));
  const translateY = values.length >= 6 ? values[5] : 0;
  return Math.max(0, wrapperHeight - translateY);
}

// How far to scroll the page when this much of the keyboard is visible.
//
// The scroll follows the keyboard proportionally, so the page moves in step
// with the sliding sheet instead of jumping when it arrives. Only as much
// scroll as the focused input actually needs is ever applied - never the
// keyboard's full height.
//
// Exported for the unit test; nothing outside this file needs it.
export function scrollAdjustmentFor(
  scrollNeededWhenFullyOpen: number,
  visibleHeight: number,
  fullKeyboardHeight: number,
): number {
  const scrollProgress = fullKeyboardHeight > 0 ? (visibleHeight / fullKeyboardHeight) : 0;
  return scrollNeededWhenFullyOpen * scrollProgress;
}

// Keeps the page behind the on-screen keyboard in step with it.
//
// While the keyboard is open, moving or closing, this runs an animation-frame
// loop that reads how much of the keyboard is visible, sets the page's bottom
// padding to match, and scrolls the container holding the focused input just
// enough to keep that input above the keyboard. The component that owns the
// keyboard drives this with start() and stop(); it has no other connection to
// the keypad, the rollers or the input sequence.
export class KeyboardScrollSync {
  private observerFrame: number | null = null;
  private lastVisibleKeyboardHeight: number = 0;
  private baseScrollTop: number = 0; // Base scroll position when keyboard starts opening
  private targetScrollAdjustment: number = 0; // How much to scroll when keyboard fully open
  private fullKeyboardHeight: number = 0; // Full keyboard height for calculating scroll progress

  constructor(
    private hostElement: HTMLElement,
    private keyboardService: VirtualKeyboardService,
  ) {}

  // Report the keyboard's full height to the service, from a ResizeObserver entry.
  onSized(rect: DOMRectReadOnly) {
    // ResizeObserver's contentRect doesn't include padding
    // Get the actual full height of the bottom sheet wrapper including padding
    const bottomSheetWrapper = this.hostElement.closest('.bottom-sheet-wrapper') as HTMLElement | null;
    if (bottomSheetWrapper) {
      // Use offsetHeight to include padding and border
      this.keyboardService.keyboardHeight = bottomSheetWrapper.offsetHeight;
    } else {
      // Fallback to rect.height if wrapper not found
      this.keyboardService.keyboardHeight = rect.height;
    }
  }

  // Start syncing the page to the keyboard's movement.
  //
  // isKeyboardShown is asked every frame: the loop keeps running through the
  // closing animation and only stops once the keyboard is both dismissed and
  // fully off the screen. Calling this while the loop already runs does nothing.
  start(targetElement: HTMLElement | undefined, isKeyboardShown: () => boolean) {
    // if the loop is already running, leave it alone
    if (this.observerFrame !== null) {
      return;
    }

    if (!targetElement) {
      return;
    }

    // Find scroll container
    const scrollContainer = findScrollContainer(targetElement);
    if (!scrollContainer) {
      return;
    }

    // Find keyboard wrapper to observe its transform (for drag/animation updates)
    const keyboardWrapper = this.hostElement.closest('.bottom-sheet-wrapper') as HTMLElement | null;
    if (!keyboardWrapper) {
      return;
    }

    // Initialize by reading actual visible height from wrapper at this moment
    // (Don't use keyboardService.keyboardHeight - it's the full height, but wrapper might be translated during opening animation)
    this.lastVisibleKeyboardHeight = visibleHeightOf(keyboardWrapper);

    // Calculate smart base scroll position based on input visibility
    // Only capture once to prevent drift across open/close cycles
    if (this.baseScrollTop === 0) {
      // Store the full keyboard height for proportional scroll calculations
      this.fullKeyboardHeight = keyboardWrapper.offsetHeight;

      // Capture the original scroll position
      this.baseScrollTop = scrollContainer.scrollTop;

      // Get input position relative to viewport
      const inputRect = targetElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Calculate where input bottom SHOULD be when keyboard is fully open
      // Add 40px margin so input isn't right at the keyboard edge
      const desiredInputBottom = viewportHeight - this.fullKeyboardHeight - 40;

      // Calculate how much scroll adjustment is needed when keyboard fully opens
      if (inputRect.bottom > desiredInputBottom) {
        // Input would be covered - need to scroll up
        this.targetScrollAdjustment = Math.max(0, inputRect.bottom - desiredInputBottom);
      } else {
        // Input already visible - minimal or no scroll needed
        this.targetScrollAdjustment = 0;
      }
    }

    // Start observation loop
    const observe = () => {
      // Check if wrapper is still in DOM
      if (!keyboardWrapper.isConnected) {
        this.observerFrame = null;
        return;
      }

      const visibleHeight = visibleHeightOf(keyboardWrapper);

      // Calculate delta from our tracked value
      const heightDelta = visibleHeight - this.lastVisibleKeyboardHeight;

      // if the visible height changed, update the padding and adjust the scroll
      if (heightDelta !== 0) {
        // Update padding to match visible keyboard height (synchronous for zero-lag)
        this.keyboardService.setKeyboardPadding(visibleHeight);

        scrollContainer.scrollTop = this.baseScrollTop +
          scrollAdjustmentFor(this.targetScrollAdjustment, visibleHeight, this.fullKeyboardHeight);

        // Track new visible height
        this.lastVisibleKeyboardHeight = visibleHeight;
      }

      // Only stop if keyboard has been dismissed AND is fully hidden
      // (Don't stop during opening animation when starting from 0)
      if (!isKeyboardShown() && visibleHeight <= 0) {
        this.observerFrame = null;
        return;
      }

      // Continue observing
      this.observerFrame = requestAnimationFrame(observe);
    };

    // Start the observation loop
    this.observerFrame = requestAnimationFrame(observe);
  }

  // Stop syncing.
  //
  // Note the tracked scroll values are cleared only when a loop was actually
  // running. When the loop instead stopped by itself - the keyboard went away
  // while it ran - they survive, so the "capture the base scroll position only
  // once" check in start() still sees the earlier value on the next open. That
  // is how this behaved before it was moved out of the keyboard component and
  // is kept deliberately.
  stop() {
    if (this.observerFrame !== null) {
      cancelAnimationFrame(this.observerFrame);
      this.observerFrame = null;
      this.lastVisibleKeyboardHeight = 0;
      this.baseScrollTop = 0;
      this.targetScrollAdjustment = 0;
      this.fullKeyboardHeight = 0;
    }
  }
}
